import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Users, FileText, TrendingUp, Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const NutritionistPanel = () => {
  const { API, user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [recommendations, setRecommendations] = useState('');

  useEffect(() => {
    if (user?.role === 'nutritionist' || user?.role === 'admin') {
      fetchPatients();
    }
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/nutritionist/patients`);
      setPatients(response.data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchPatientDetails = async (patientId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/nutritionist/patient/${patientId}`);
      setPatientDetails(response.data);
      setSelectedPatient(patientId);
    } catch (error) {
      toast.error('Eroare la încărcarea detaliilor pacientului');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/nutritionist/notes`, {
        patient_id: selectedPatient,
        note: noteText,
        recommendations: recommendations,
      });

      toast.success('Notița a fost adăugată cu succes!');
      setNoteText('');
      setRecommendations('');
      fetchPatientDetails(selectedPatient);
    } catch (error) {
      toast.error('Eroare la adăugarea notiței');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'nutritionist' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <Navbar />
        <div className="container mx-auto px-6 md:px-12 py-8">
          <Card className="border-none card-shadow">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">Acces Restricționat</h3>
              <p className="text-[#4a5568]">Această pagină este disponibilă doar pentru nutriționiști</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 py-8" data-testid="nutritionist-panel">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
            Panou Nutriționist
          </h1>
          <p className="text-[#4a5568] text-lg mb-8">
            Gestionează pacienții și oferă recomandări personalizate
          </p>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Patients List */}
            <Card className="lg:col-span-1 border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Users className="w-6 h-6 mr-2" />
                  Pacienți ({patients.length})
                </CardTitle>
                <CardDescription>Listă pacienți activi</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {patients.map((patient) => (
                    <div
                      key={patient.id}
                      onClick={() => fetchPatientDetails(patient.id)}
                      data-testid={`patient-${patient.id}`}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedPatient === patient.id
                          ? 'bg-[#1A4D2E] text-white'
                          : 'bg-[#F5F5F0] hover:bg-[#E2E8F0]'
                      }`}
                    >
                      <p className="font-medium">{patient.email}</p>
                      <p className="text-sm opacity-80">ID: {patient.id.substring(0, 8)}...</p>
                    </div>
                  ))}
                  {patients.length === 0 && (
                    <p className="text-center text-[#4a5568] py-8">Nu există pacienți încă</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Patient Details */}
            <div className="lg:col-span-2 space-y-6">
              {patientDetails ? (
                <>
                  {/* Profile & Metrics */}
                  <Card className="border-none card-shadow">
                    <CardHeader>
                      <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center justify-between">
                        <span className="flex items-center">
                          <TrendingUp className="w-6 h-6 mr-2" />
                          Profil Pacient
                        </span>
                        <Badge className="bg-[#9BCF53] text-white">
                          {patientDetails.metrics?.length || 0} metrici
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-[#F5F5F0] p-4 rounded-xl">
                          <p className="text-sm text-[#4a5568] mb-1">Greutate</p>
                          <p className="text-xl font-semibold text-[#1A4D2E]">
                            {patientDetails.profile?.weight || 'N/A'} kg
                          </p>
                        </div>
                        <div className="bg-[#F5F5F0] p-4 rounded-xl">
                          <p className="text-sm text-[#4a5568] mb-1">Înălțime</p>
                          <p className="text-xl font-semibold text-[#1A4D2E]">
                            {patientDetails.profile?.height || 'N/A'} cm
                          </p>
                        </div>
                        <div className="bg-[#F5F5F0] p-4 rounded-xl">
                          <p className="text-sm text-[#4a5568] mb-1">Vârstă</p>
                          <p className="text-xl font-semibold text-[#1A4D2E]">
                            {patientDetails.profile?.age || 'N/A'} ani
                          </p>
                        </div>
                      </div>

                      {/* Health Conditions */}
                      {patientDetails.profile?.medical_conditions?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-[#4a5568] mb-2">Afecțiuni Medicale:</p>
                          <div className="flex flex-wrap gap-2">
                            {patientDetails.profile.medical_conditions.map((condition, i) => (
                              <Badge key={i} className="bg-orange-100 text-orange-800">
                                {condition}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dietary Preferences */}
                      {patientDetails.profile?.dietary_preferences?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-[#4a5568] mb-2">Preferințe Alimentare:</p>
                          <div className="flex flex-wrap gap-2">
                            {patientDetails.profile.dietary_preferences.map((pref, i) => (
                              <Badge key={i} className="bg-[#1A4D2E] text-white">
                                {pref}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Add Note */}
                  <Card className="border-none card-shadow">
                    <CardHeader>
                      <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                        <FileText className="w-6 h-6 mr-2" />
                        Adaugă Notă
                      </CardTitle>
                      <CardDescription>Recomandări și observații pentru pacient</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleAddNote} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="note">Notă</Label>
                          <Textarea
                            id="note"
                            data-testid="note-textarea"
                            placeholder="Observații despre starea pacientului..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            required
                            rows={4}
                            className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="recommendations">Recomandări</Label>
                          <Textarea
                            id="recommendations"
                            data-testid="recommendations-textarea"
                            placeholder="Recomandări nutriționale specifice..."
                            value={recommendations}
                            onChange={(e) => setRecommendations(e.target.value)}
                            rows={3}
                            className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={loading}
                          data-testid="add-note-button"
                          className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {loading ? 'Se adaugă...' : 'Adaugă Notă'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  {/* Notes History */}
                  {patientDetails.notes?.length > 0 && (
                    <Card className="border-none card-shadow">
                      <CardHeader>
                        <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                          Istoric Notițe ({patientDetails.notes.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {patientDetails.notes.map((note) => (
                            <div key={note.id} className="bg-[#F5F5F0] p-4 rounded-xl">
                              <div className="flex items-center justify-between mb-2">
                                <Badge className="bg-[#9BCF53] text-white text-xs">
                                  {new Date(note.created_at).toLocaleDateString('ro-RO')}
                                </Badge>
                              </div>
                              <p className="text-sm text-[#1A4D2E] mb-2">{note.note}</p>
                              {note.recommendations && (
                                <p className="text-xs text-[#4a5568] italic">
                                  Recomandări: {note.recommendations}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="p-12 text-center">
                    <Eye className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#4a5568]">Selectează un pacient pentru a vedea detaliile</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NutritionistPanel;
